import { ContentWrapper } from '../components/design-system/ContentWrapper';
import { useCreditCardState } from '../hooks/useCreditCardState';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useWalletCategories } from '../hooks/useWalletCategories';
import { useCardModal } from '../hooks/useCardModal';
import { useShareModal } from '../hooks/useShareModal';
import { CreditCardSearchBar } from '../components/CreditCardSearchBar';
import { RewardsCardPreviews } from './RewardsEstimator';
import { FooterContent } from 'components/FooterContent';
import { Navigation } from '../components/Navigation';
import { SelectedCardsIndicator } from '../components/SelectedCardsIndicator';
import { useScreenSize } from '../hooks/useScreenSize';
import { saveWallet } from '../services/walletShareApi';
import { useCategoryEarnRatesAndCreditsV2 } from '../data/shared/hooks/useBestEarnRatesV2';
import { usePointValues } from 'data/shared/hooks/usePointValues';
import { CategoryType } from 'data/shared/__autogenerated__/types/CreditCardCategoryTypes';
import { useURLParamPromotionalTypes } from '../hooks/useURLParamPromotionalTypes';
import { useHeroTitleAndDescription } from '../hooks/useHeroTitleAndDescription';
import { CreditCardData } from '../data/shared/CreditCardData';
import { usePageMeta } from '../hooks/usePageMeta';

// const DESCRIPTION_TEXT =
//   'See how your credit cards can work together and discover ways to improve your wallet. We gather data from many sources, but it may not always be the latest. Double-check important details.';

// Categories that should be shown in the homepage pills
const CATEGORIES_TO_SHOW: CategoryType[] = [
  CategoryType.ALL_PURCHASES,
  CategoryType.GAS,
  CategoryType.GROCERY,
  CategoryType.TRAVEL,
  CategoryType.DINING,
  CategoryType.DRUGSTORE,
];

// All categories for Reddit export
const ALL_CATEGORIES = Object.values(CategoryType);

export const HomePage = () => {
  const [selectedCards, setSelectedCards] = useCreditCardState();
  const {
    categoriesByIndex,
    handleAddCard,
    handleRemoveCard,
    handleRemoveAll,
    handleSelectCategoriesForCard,
  } = useWalletCategories(selectedCards, setSelectedCards);

  usePageMeta({
    title: 'CardClues - Find the Best Credit Card for Every Purchase',
    description:
      'Compare credit card rewards and build your ideal wallet. See which card earns the most for dining, travel, groceries, gas, and more.',
    canonicalPath: '/',
  });

  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const screenSize = useScreenSize();
  const isMobile = screenSize === 'mobile';

  // Shared savedUrl state — survives across mobile/desktop layout switches
  const [savedUrl, setSavedUrl] = useState<string | null>(() => {
    const pathMatch = window.location.pathname.match(/^\/w\/(.+)$/);
    return pathMatch ? `${window.location.origin}/w/${pathMatch[1]}` : null;
  });
  const prevCardsKeyRef = useRef(selectedCards.join(','));

  // Clear savedUrl when cards change so a new link is generated
  useEffect(() => {
    const cardsKey = selectedCards.join(',');
    if (prevCardsKeyRef.current !== cardsKey) {
      prevCardsKeyRef.current = cardsKey;
      setSavedUrl(null);
    }
  }, [selectedCards]);

  // Always generate a link when savedUrl is null (on mount or after card changes)
  useEffect(() => {
    if (savedUrl !== null) {
      return;
    }
    let cancelled = false;
    saveWallet(selectedCards)
      .then((code) => {
        if (!cancelled) {
          setSavedUrl(`${window.location.origin}/w/${code}`);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSavedUrl(window.location.href);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedUrl]);

  // Get centralized promotional types state (hydrates from URL on mount, then writes to URL)
  const { promotionalTypes, setPromotionalTypes, resetPromotionalTypes } =
    useURLParamPromotionalTypes(selectedCards);

  // Get point values with default values
  const [pointValuesOverrides, setPointValuesOverrides] = useState<
    Record<string, number>
  >({});
  const { pointValues } = usePointValues(
    selectedCards,
    false,
    setPointValuesOverrides,
    pointValuesOverrides
  );

  // Get best earn rates for pill display categories (with current settings)
  const categoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    selectedCards,
    CATEGORIES_TO_SHOW,
    pointValues as Record<string, number>,
    true, // Assume purchasing in USA
    categoriesByIndex,
    promotionalTypes
  );

  // Get full earn rates for Reddit export (all categories, with current settings)
  const fullCategoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    selectedCards,
    ALL_CATEGORIES,
    pointValues as Record<string, number>,
    true, // Assume purchasing in USA
    categoriesByIndex,
    promotionalTypes
  );

  // Get default earn rates for Reddit export (all categories, no promo types, no selected categories)
  const defaultFullCategoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    selectedCards,
    ALL_CATEGORIES,
    pointValues as Record<string, number>,
    true, // Assume purchasing in USA
    {}, // No selected categories
    [] // No promotional types
  );

  // Build pills data for UI display (simplified - just for the sticky header)
  const categoryPills = useMemo(() => {
    if (selectedCards.length === 0) {
      return [];
    }

    return CATEGORIES_TO_SHOW.map((category) => {
      const categoryData = categoryEarnRates[category];
      const bestCard = categoryData?.sortedCardsByEarnRate[0];

      if (!bestCard || !bestCard.earnRate) {
        return null;
      }

      const cardData = CreditCardData[bestCard.card];
      return {
        category,
        earnRate: bestCard.earnRate,
        rewardsType: bestCard.rewardsType,
        cardName: cardData?.name ?? 'this card',
      };
    }).filter((pill): pill is NonNullable<typeof pill> => pill !== null);
  }, [categoryEarnRates, selectedCards.length]);

  const [cardModal, setShowCardModal] = useCardModal({
    handleRemoveCard,
    handleAddCard,
    selectedCards,
    onSelectCategoriesForCard: handleSelectCategoriesForCard,
    currentSelectedCategories: categoriesByIndex,
    promotionalTypes,
    setPromotionalTypes,
    resetPromotionalTypes,
  });
  const [shareModal] = useShareModal();
  const { title, description, author } =
    useHeroTitleAndDescription(selectedCards);

  // Detect when search bar becomes sticky
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: [1] }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      {shareModal}
      <Navigation />

      <ContentWrapper
        style={{
          position: 'relative',
          paddingBottom: '0 !important',
          width: 'initial',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            maxWidth: '1200px',
            margin: '0 auto',
          }}
        >
          <h1
            style={{
              fontSize: 'clamp(28px, 5vw, 48px)',
              fontWeight: 300,
              margin: '0 0 16px 0',
              color: '#1a1a1a',
              lineHeight: 1.2,
            }}
          >
            {title}
          </h1>
          <p
            style={{
              color: '#5D6C70',
              margin: '0 auto 32px auto',
              maxWidth: '500px',
              lineHeight: 1.6,
            }}
          >
            {description}
          </p>
          {author && (
            <p
              style={{
                color: '#000',
                margin: '0 auto 32px auto',
                maxWidth: '500px',
                fontSize: '14px',
              }}
            >
              {author}
            </p>
          )}
        </div>

        {/* Sentinel element to detect when search bar becomes sticky */}
        <div ref={sentinelRef} style={{ height: '1px' }} />

        <div
          style={{
            position: 'sticky',
            top: '5px',
            zIndex: 1000,
            margin: '15px auto 15px auto',
          }}
        >
          <div className={`search-bar-inner ${isSticky ? 'is-stuck' : ''}`}>
            <CreditCardSearchBar
              handleAddCard={handleAddCard}
              handleRemoveCard={handleRemoveCard}
              selectedCards={selectedCards}
              setSelectedCards={setSelectedCards}
            />
          </div>
        </div>
        <style>
          {`
            .search-bar-inner {
              transition: ${isMobile ? 'none' : 'all 0.3s ease'};
            }
            
            .search-bar-inner .gradient-border {
              transition: ${isMobile ? 'none' : 'all 0.3s ease'};
            }
            
            .search-bar-inner .gradient-border input {
              transition: ${isMobile ? 'none' : 'all 0.3s ease'};
            }
            
            .search-bar-inner .gradient-border input::placeholder {
              transition: ${isMobile ? 'none' : 'all 0.3s ease'};
            }

            ${
              !isMobile
                ? `
            .search-bar-inner.is-stuck .search-bar-container {
              padding-top: 5px !important;
              padding-bottom: 5px !important;
            }
            `
                : ''
            }
            
            .search-bar-inner.is-stuck .gradient-border input::placeholder {
            }
          `}
        </style>

        <SelectedCardsIndicator
          selectedCards={selectedCards}
          onCardClick={(card, walletIndex) =>
            setShowCardModal(
              walletIndex !== undefined ? { card, walletIndex } : card
            )
          }
          onRemoveAll={handleRemoveAll}
          categoryPills={categoryPills}
          categoryEarnRates={fullCategoryEarnRates}
          defaultCategoryEarnRates={defaultFullCategoryEarnRates}
          savedUrl={savedUrl}
          setSavedUrl={setSavedUrl}
        />

        <div
          style={{
            position: 'absolute',
            top: 0,
            paddingTop: '200px',
            left: 0,
            right: 0,
            height: '800px',
            zIndex: -1,
            background:
              'linear-gradient(to bottom, #fff 0%, #fff 30%, #eef1f4 100%)',
            pointerEvents: 'none',
          }}
        ></div>

        <div style={{ marginTop: 40, position: 'relative', paddingBottom: 40 }}>
          <RewardsCardPreviews
            cards={selectedCards}
            handleRemoveCard={handleRemoveCard}
            handleAddCard={handleAddCard}
            setSelectedCards={setSelectedCards}
            promotionalTypes={promotionalTypes}
            setPromotionalTypes={setPromotionalTypes}
            resetPromotionalTypes={resetPromotionalTypes}
            categoriesByIndex={categoriesByIndex}
            onSelectCategoriesForCard={handleSelectCategoriesForCard}
          />
        </div>

        {cardModal}
      </ContentWrapper>
      <div style={{ padding: 15 }}>
        <FooterContent />
      </div>
    </div>
  );
};
